<template>
  <div class="dashboard-content">
    <div>
      <h5 v-if="$route.name.includes('edit')">{{ $t('global.successful_save') }}</h5>
      <h5 v-else>{{ $t('feeds.feed_is_ready') }}</h5>
    </div>
    <div v-if="isError" class="py-4 my-5">
      <img class="icon-small" v-bind:src="'/app/images/icons/error.svg'" />
      <strong class="text-danger mb-0 mx-3">{{ $t('feeds.feed_get_info_failed')}}</strong>
    </div>
    <div v-else-if="isLoading" class="mt-5">
      <p>{{ $t('feeds.saving')}}</p>
      <div class="loading-bar mt-5">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
    </div>
    <div v-else class="d-flex mt-5">
      <div class="w-50 pt-2">
        <p><strong>{{ $t('global.includes') }}: </strong>{{ finishedFeed.logs[0].productCount }}  {{$t('products.products2')}}</p>
        <div class="d-flex pt-3">
          <a :href="feedsDirectoryUrl + finishedFeed.id+'.'+finishedFeed.config.resultType" target="_blank" class="mr-2 pr-3">
            <link-icon></link-icon>
            <span class="ml-2">{{ $t('feeds.open_feed') }}</span>
          </a>
          <div :id="'copy-feed-url-'+finishedFeed.id" class="d-flex align-items-center mb-0 ml-4 cursor-pointer copy-link-container" v-clipboard:copy="feedsDirectoryUrl + finishedFeed.id+'.'+finishedFeed.config.resultType" v-clipboard:success="onCopy('copy-feed-url-'+finishedFeed.id)">
            <copy-icon></copy-icon>
            <p class="ml-2 mb-1">{{ $t('feeds.copy_link') }}</p>
          </div>
          <b-popover :target="'copy-feed-url-'+finishedFeed.id" variant="success" triggers="click" placement="bottomleft">
            <div>
              <p class="mb-0">{{ $t('feeds.url_copied_to_clipboard') }}</p>
            </div>
          </b-popover>
        </div>
      </div>
      <div class="w-50 ml-5 pl-5">
        <div v-if="criticalAlerts.length > 0" class="alert alert-danger w-100 mr-5 pb-2 mb-4" role="alert">
          <h6 class="alert-heading">{{ $t('.global.alerts2') }} {{ $t('global.alerts_critical')}} ({{ criticalAlerts.length }})</h6>
          <hr>
          <ul v-for="(alert, index) in criticalAlerts" v-bind:key="index">
            <li>
              <p class="mb-0">{{ alert.message }}</p>
            </li>
          </ul>
        </div>
        <div v-if="warningAlerts.length > 0" class="alert alert-warning w-100 mr-5 pb-2 mb-4" role="alert">
          <h6 class="alert-heading">{{ $t('.global.alerts2') }} {{ $t('global.alerts_informative')}} ({{ warningAlerts.length }})</h6>
          <hr>
          <ul v-for="(alert, index) in warningAlerts" v-bind:key="index">
            <li>
              <p class="mb-0">{{ alert.message }}</p>
            </li>
          </ul>
        </div>
        <div v-if="alerts.length === 0" class="alert alert-success d-flex align-items-center w-100 mr-5 pt-4 pb-4 mb-4" role="alert">
          <img class="icon" src="/app/images/icons/success.svg" />
          <p class="mb-0">{{  $t('feeds.feed_no_alert') }}</p>
        </div>
        <router-link :to="{ name: 'feed.details', params: { id: this.$attrs.projectId, feedId: finishedFeed.id, feed: finishedFeed }}">
          <button type="button" class="btn btn-medium btn-orange">
            <info-icon></info-icon>
            <span>{{ $t('feeds.more_info') }}</span>
          </button>
        </router-link>
      </div>
    </div>
    <div class="mt-5">
      <h5 class="mb-4">{{ $t('global.preview') }}</h5>
      <table class="table table-striped table-bordered table-feed-final">
        <div>
          <thead class="thead-light">
            <th v-for="(value, field) in products[0]" v-bind:key="field">
              <div class="d-flex justify-content-center align-items-center">
                <div>{{ field }}</div>
              </div>
            </th>
          </thead>
          <tbody>
            <tr v-for="(product, index) in products" v-bind:key="index">
              <td v-for="(value, field) in product" v-bind:key="field">
                <p class="break-text">{{ value }}</p>
              </td>
            </tr>
          </tbody>
        </div>
      </table>
    </div>
  </div>
</template>

<script>
import { CopyIcon, InfoIcon, LinkIcon } from 'vue-feather-icons'
import axios from 'axios'

export default {
  name: 'Finish',

  data () {
    return {
      alerts: [],
      feedsDirectoryUrl: process.env.VUE_APP_FEEDS_DIRECTORY,
      finishedFeed: {},
      isError: false,
      isLoading: true
    }
  },

  props: {
    feed: { required: true, type: Object },
    feedId: { required: true, type: String },
    products: { required: true, type: Array }
  },

  mounted () {
    this.$nextTick(function () {
      window.setInterval(() => {
        if (this.isLoading) this.getFeed(this.$attrs.projectId)
      }, 5000)
    })
  },

  methods: {
    getFeed () {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/data/feed/' + this.$attrs.projectId + '/' + this.feedId)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            if (response.data.logs.length > 0) {
              this.finishedFeed = response.data
              this.getProjectAlerts(this.$attrs.projectId)
              const matchingFeed = this.$attrs.project.feeds.filter(f => f.id === response.data.id)[0]
              matchingFeed.logs = response.data.logs
            }
          }
        })
        .catch((error) => {
          if (error.response) {
            this.isError = true
            this.isLoading = false
          }
        })
    },

    getProjectAlerts (projectId) {
      axios.get(process.env.VUE_APP_API_ENDPOINT + '/alerts/' + projectId)
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            if (response.data.feed !== undefined) this.alerts = response.data.feed.filter(feed => feed.feedId === this.finishedFeed.id)
            this.isLoading = false
          }
        })
        .catch((error) => {
          if (error.response) {
            this.$router.push({ name: 'auth.error', params: { errorCode: error.response.data.statusCode, errorMessage: error.response.data.message } })
          }
        })
    },

    onCopy (popoverId) {
      setTimeout(function () {
        this.$root.$emit('bv::hide::popover', popoverId)
      }.bind(this), 1500)
    }
  },

  computed: {
    criticalAlerts () {
      return this.alerts.filter(alert => alert.level === 0)
    },

    warningAlerts () {
      return this.alerts.filter(alert => alert.level === 1)
    }
  },

  components: { CopyIcon, InfoIcon, LinkIcon }

}
</script>
